/**
 * FriendProfile Page (/profile/:username)
 *
 * Echte Profilseite für andere User (Freunde etc.) - wie die eigene Profilseite,
 * aber schreibgeschützt und privacy-aware: GET /users/{id} liefert serverseitig
 * bereits nur die Felder, die der jeweilige show_*-Privacy-Schalter erlaubt
 * (fehlendes Feld = nicht erlaubt). Zusätzlich werden erlaubte, aber leere
 * Felder hier ausgeblendet statt mit "Nicht angegeben" angezeigt.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Profile.module.css';
import wlStyles from './Wishlist.module.css';
import { userApi, friendsApi, getMediaUrl } from '../api';
import { getSocialPlatform } from '../utils/socialLinks';
import { useConfirm } from '../context/ConfirmContext';

function FriendProfile() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { username } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [profile, setProfile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [library, setLibrary] = useState([]);
  const [message, setMessage] = useState(null);

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    setIsPrivate(false);
    setProfile(null);
    setLibrary([]);

    try {
      // Username -> user_id auflösen (GET /users/{id} erwartet eine ID)
      const searchResult = await userApi.searchUsers(username);
      const match = searchResult.success
        ? (searchResult.data?.items || []).find(
            (u) => u.username?.toLowerCase() === username.toLowerCase()
          )
        : null;

      if (!match) {
        setNotFound(true);
        return;
      }

      const profileResult = await userApi.getUserById(match.id);
      if (profileResult.success && profileResult.data) {
        const user = profileResult.data;
        setProfile(user);
        if (user.avatar_has && user.avatar_media_key) {
          setAvatarUrl(getMediaUrl(user.avatar_media_key));
        }
        // Sammlungsvorschau ist nur für Freunde verfügbar - bestes Bemühen,
        // schlägt für Nicht-Freunde mit 403 fehl und wird dann einfach ausgeblendet
        friendsApi.getFriendLibrary(match.id, { limit: 24 }).then((libResult) => {
          if (libResult.success) setLibrary(libResult.data || []);
        }).catch(() => {});
      } else {
        const code = profileResult.error?.code;
        if (code === 'PRIVATE_RESOURCE' || profileResult.error?.status === 403) {
          setIsPrivate(true);
        } else {
          setNotFound(true);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden des Freundprofils:', err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleRemoveFriend = async () => {
    if (!profile?.id) return;
    if (!(await confirm(t('friendProfile.removeConfirm', { username: profile.username }), { danger: true }))) return;
    try {
      const result = await friendsApi.removeFriend(profile.id);
      if (result.success) {
        navigate('/friends');
      } else {
        setMessage({ type: 'error', text: result.error?.message || t('friendProfile.removeError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('friendProfile.networkError') });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.statsSection}>
          <h2>😕 {t('friendProfile.userNotFound')}</h2>
          <p>{t('friendProfile.userNotFoundText', { username })}</p>
          <Link to="/friends" className={styles.editBtn}>← {t('friendProfile.backToFriends')}</Link>
        </div>
      </div>
    );
  }

  if (isPrivate) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.statsSection}>
          <h2>🔒 {t('friendProfile.privateProfile')}</h2>
          <p>{t('friendProfile.privateProfileText', { username })}</p>
          <Link to="/friends" className={styles.editBtn}>← {t('friendProfile.backToFriends')}</Link>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const genres = profile.favorite_genres || [];
  const socialLinks = profile.social_links || [];
  const hasStats = profile.manga_count != null || profile.band_count != null
    || profile.favorite_manga || profile.read_manga_count != null
    || Array.isArray(profile.wishlist);

  return (
    <div className={styles.profileContainer}>
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          <span>❌</span>
          <p>{message.text}</p>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <Link to="/friends" className={styles.malLink} style={{ marginBottom: '1rem', display: 'inline-block' }}>
        ← {t('friendProfile.backToFriends')}
      </Link>

      {/* Profile Header */}
      <div className={styles.profileHeader}>
        <div className={styles.avatarSection}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className={styles.avatarLarge} onError={() => setAvatarUrl(null)} />
          ) : (
            <div className={styles.avatarLarge}>{profile.username?.charAt(0).toUpperCase()}</div>
          )}
        </div>
        <div className={styles.headerInfo}>
          <h1>{profile.username}</h1>
          {profile.bio && <p className={styles.userBio}>{profile.bio}</p>}
        </div>
      </div>

      {/* Profile Content - nur ausgefüllte, erlaubte Felder */}
      {(profile.bio || socialLinks.length > 0 || genres.length > 0) && (
        <div className={styles.profileContent}>
          <div className={styles.contentHeader}>
            <h2>{t('profile.profileInfo')}</h2>
          </div>
          <div className={styles.infoList}>
            {profile.bio && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('profile.bio')}</span>
                <span className={styles.infoValue}>{profile.bio}</span>
              </div>
            )}

            {socialLinks.length > 0 && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('profile.socialLinks')}</span>
                <span className={styles.infoValue}>
                  <div className={styles.socialLinksList}>
                    {socialLinks.map((link, index) => {
                      const platform = getSocialPlatform(link);
                      const Icon = platform?.Icon;
                      return (
                        <a
                          key={index}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.socialLinkRow}
                        >
                          {Icon && (
                            <Icon className={styles.socialLinkIcon} style={platform.color ? { color: platform.color } : undefined} />
                          )}
                          <span className={styles.socialLinkPlatform}>
                            {platform?.label || t('profile.socialLinkOther')}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </span>
              </div>
            )}

            {genres.length > 0 && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('profile.favoriteGenres')}</span>
                <span className={styles.infoValue}>
                  <div className={styles.genreTags}>
                    {genres.map((genre, index) => (
                      <span key={index} className={styles.genreTag}>{genre}</span>
                    ))}
                  </div>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats - nur vorhandene Felder */}
      {hasStats && (
        <div className={styles.statsSection}>
          <h2>📊 {t('profile.statistics')}</h2>
          <div className={styles.statsGrid}>
            {profile.manga_count != null && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>📚</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{profile.manga_count}</div>
                  <div className={styles.statLabel}>{t('profile.mangaSeries')}</div>
                </div>
              </div>
            )}
            {profile.band_count != null && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>📖</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{profile.band_count}</div>
                  <div className={styles.statLabel}>{t('profile.totalVolumes')}</div>
                </div>
              </div>
            )}
            {profile.read_manga_count != null && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>📗</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{profile.read_manga_count}</div>
                  <div className={styles.statLabel}>{t('profile.seriesFullyRead')}</div>
                </div>
              </div>
            )}
            {Array.isArray(profile.wishlist) && (
              <div className={styles.statCard}>
                <div className={styles.statIcon}>💫</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{profile.wishlist.length}</div>
                  <div className={styles.statLabel}>{t('profile.onWishlist')}</div>
                </div>
              </div>
            )}
          </div>

          {profile.favorite_manga && (
            <div className={styles.infoRow} style={{ marginTop: '1rem' }}>
              <span className={styles.infoLabel}>{t('friendProfile.favoriteManga')}</span>
              <span className={styles.infoValue}>
                {profile.favorite_manga.cover_media_key && (
                  <img
                    src={getMediaUrl(profile.favorite_manga.cover_media_key)}
                    alt={profile.favorite_manga.title}
                    style={{ width: 32, height: 44, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle', marginRight: '0.5rem' }}
                  />
                )}
                {profile.favorite_manga.title}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Sammlungsvorschau - nur wenn (als Freund) zugänglich, im Stil von "Mein Regal" (abgespeckt/kleiner) */}
      {library.length > 0 && (
        <div className={styles.statsSection}>
          <h2>📘 {t('friendProfile.collection')}</h2>
          <div className={styles.miniCardGrid}>
            {library.map((manga) => {
              const total = manga.total_bands ?? 0;
              const owned = manga.owned_bands ?? 0;
              return (
                <div key={manga.id || manga.manga_id} className={styles.miniCard} title={manga.title}>
                  {manga.cover_media_key ? (
                    <img
                      src={getMediaUrl(manga.cover_media_key)}
                      alt={manga.title}
                      className={styles.miniCardCover}
                    />
                  ) : (
                    <div className={styles.miniCardCoverPlaceholder} />
                  )}
                  <div className={styles.miniCardBody}>
                    <span className={styles.miniCardTitle}>{manga.title}</span>
                    {total > 0 && (
                      <span className={styles.miniCardProgress}>{t('friendProfile.ownedOfTotal', { owned, total })}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Wishlist - nur wenn show_wishlist=true (Backend liefert profile.wishlist dann mit) */}
      {Array.isArray(profile.wishlist) && profile.wishlist.length > 0 && (
        <div className={styles.statsSection}>
          <h2>💫 {t('friendProfile.wishlistTitle')}</h2>
          <div className={wlStyles.wishlistList}>
            {profile.wishlist.map((item, index) => (
              <div key={item.wishlist_id || index} className={wlStyles.wishlistRow}>
                <div className={wlStyles.wishlistRowCoverWrap}>
                  {(item.band_cover_media_key || item.manga_cover_media_key) ? (
                    <img
                      src={getMediaUrl(item.band_cover_media_key || item.manga_cover_media_key)}
                      alt={item.manga_title}
                      className={wlStyles.wishlistRowCover}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className={wlStyles.placeholderCoverSmall} />
                  )}
                </div>
                <div className={wlStyles.wishlistRowInfo}>
                  <h3 title={item.manga_title}>{item.manga_title || '—'}</h3>
                  {item.band_number != null ? (
                    <span className={wlStyles.bandNumber}>{t('wishlist.bandLabel', { number: item.band_number })}</span>
                  ) : (
                    <span className={wlStyles.bandNumberAuto}>{t('wishlist.nextBand')}</span>
                  )}
                  {item.band_title && item.band_title !== item.manga_title && (
                    <span className={wlStyles.bandTitleInline} title={item.band_title}>{item.band_title}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.statsSection}>
        <button className={styles.editBtn} onClick={handleRemoveFriend}>
          {t('friendProfile.removeFriend')}
        </button>
      </div>
    </div>
  );
}

export default FriendProfile;
